import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHomepageHydration } from "./HomepageRenderContext";
import { LANGUAGE_OPTIONS, TRANSLATIONS } from "../utils/translations";

const DEFAULT_LANGUAGE = "en";
const STORAGE_KEY = "ecoTour.language";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const hydrating = useHomepageHydration();
  const [preferredLanguage, setLanguage] = useState(() => {
    if (typeof window === "undefined") {
      return DEFAULT_LANGUAGE;
    }
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && TRANSLATIONS[stored]) {
      return stored;
    }
    const browserLanguage = window.navigator?.language?.slice(0, 2);
    if (browserLanguage && TRANSLATIONS[browserLanguage]) {
      return browserLanguage;
    }
    return DEFAULT_LANGUAGE;
  });

  const language = hydrating ? DEFAULT_LANGUAGE : preferredLanguage;

  useEffect(() => {
    if (hydrating) return;
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, language);
    }
  }, [hydrating, language]);

  const translate = useCallback(
    (key, fallback) => {
      if (!key) return fallback ?? "";
      const current = TRANSLATIONS[language] ?? {};
      const english = TRANSLATIONS.en ?? {};
      if (Object.prototype.hasOwnProperty.call(current, key)) {
        return current[key];
      }
      if (Object.prototype.hasOwnProperty.call(english, key)) {
        return english[key];
      }
      return fallback ?? key;
    },
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      availableLanguages: LANGUAGE_OPTIONS,
      t: translate,
    }),
    [language, translate]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
